import React from 'react';
import { Link } from 'react-router-dom';

import titleImg from '../../assets/img/logo-lockup.svg';

// Self-contained Terms of Service. Does NOT use the retired marketing
// Header/NavFooter. Reachable at /terms without authentication. Written to
// modern app-store standards: store-based subscription disclosures,
// Apple App Store EULA clauses, and the app's actual behavior.

const LAST_UPDATED = 'June 5, 2026';

const FONT_BODY = '"Hanken Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const FONT_DISPLAY = '"Fredoka", system-ui, sans-serif';

const styles = {
	page: {
		fontFamily: FONT_BODY,
		color: '#352F27',
		lineHeight: 1.6,
		backgroundColor: '#FBF7F0',
		minHeight: '100vh',
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 16,
		maxWidth: 760,
		margin: '0 auto',
		padding: '24px 24px 0',
	},
	logo: { height: 34 },
	back: { color: '#D86641', fontWeight: 700, textDecoration: 'none' },
	container: { maxWidth: 760, margin: '0 auto', padding: '8px 24px 64px' },
	h1: { fontFamily: FONT_DISPLAY, fontSize: 34, fontWeight: 600, marginBottom: 4, color: '#1E1A15' },
	updated: { color: '#6B5F4D', marginTop: 0 },
	h2: { fontFamily: FONT_DISPLAY, fontSize: 24, fontWeight: 600, marginTop: 36, color: '#1E1A15' },
	a: { color: '#D86641' },
	summary: {
		background: '#FBF0EB',
		borderLeft: '4px solid #D86641',
		borderRadius: 12,
		padding: '4px 20px',
		margin: '24px 0',
	},
};

const Terms = () => {
	return (
		<div style={styles.page}>
			<div style={styles.header}>
				<Link to="/">
					<img src={titleImg} alt="Italian Naturally" style={styles.logo} />
				</Link>
				<Link to="/" style={styles.back}>← Home</Link>
			</div>

			<div style={styles.container}>
				<h1 style={styles.h1}>Terms of Service</h1>
				<p style={styles.updated}>Last updated: {LAST_UPDATED}</p>

				<p>
					These Terms of Service (&quot;Terms&quot;) are a contract between you
					and Andrew Bogaard, operating Italian Naturally (&quot;we&quot; or
					&quot;us&quot;), governing your use of the Italian Naturally mobile app
					and website (the &quot;Service&quot;). By creating
					an account or using the Service, you agree to these Terms and to our{' '}
					<Link to="/privacy" style={styles.a}>Privacy Policy</Link>. If you
					don&apos;t agree, don&apos;t use the Service.
				</p>

				<div style={styles.summary}>
					<p><strong>The short version</strong></p>
					<ul>
						<li>The first lesson is free; the full library needs a subscription bought through the App Store or Google Play.</li>
						<li>Subscriptions renew automatically until you cancel in your store account; refunds are handled by Apple/Google.</li>
						<li>The app is for your personal learning. Don&apos;t copy, resell, or reverse-engineer it.</li>
						<li>You keep ownership of your recordings; you let us process them on your device to score you.</li>
						<li>The Service is provided &quot;as is,&quot; and our liability is limited as described below.</li>
					</ul>
				</div>

				<h2 style={styles.h2}>Who Can Use the Service</h2>
				<p>
					You must be at least 13 years old. If you are under the age of majority
					where you live, you may use the Service only with the involvement of a
					parent or guardian. By using it, you confirm you meet these
					requirements and that the information you give us is accurate.
				</p>

				<h2 style={styles.h2}>Your Account</h2>
				<p>
					You&apos;re responsible for your account credentials and for activity
					under your account. Keep your password confidential, don&apos;t share
					your account, and tell us promptly about any unauthorized use.
				</p>

				<h2 style={styles.h2}>Subscriptions, Billing, and Cancellation</h2>
				<p>
					The first lesson is free. Access to the full library of conversations
					and courses requires a paid subscription purchased through the{' '}
					<strong>Apple App Store</strong> or <strong>Google Play</strong>
					(&quot;the Stores&quot;).
				</p>
				<ul>
					<li>Payment is charged to your Store account when you confirm the purchase.</li>
					<li>
						Subscriptions <strong>renew automatically</strong> for the same
						period and price unless you cancel at least 24 hours before the
						current period ends. Your Store account is charged for renewal within
						the 24 hours before the period ends.
					</li>
					<li>
						<strong>Manage or cancel</strong> your subscription anytime in your
						Apple or Google account settings — cancellation is not done inside the
						app, and uninstalling the app does not cancel a subscription.
					</li>
					<li>
						<strong>Refunds</strong> are governed by the Stores&apos; policies; we
						generally cannot grant refunds directly. (Where the law gives you a
						withdrawal or refund right, that right still applies.)
					</li>
					<li>We may change prices or plans; we&apos;ll give notice where required, and changes apply to future billing periods.</li>
				</ul>

				<h2 style={styles.h2}>License and Intellectual Property</h2>
				<p>
					We grant you a personal, non-exclusive, non-transferable, revocable
					license to use the Service for your own, non-commercial language
					learning. The Service and all of its content — audio, text, software,
					and design — belong to us or our licensors and are protected by
					intellectual-property laws. You may not sell, sublicense, distribute,
					or publicly perform the content except as the Service intends.
				</p>

				<h2 style={styles.h2}>Your Recordings</h2>
				<p>
					The Service records short audio of you speaking to score your
					pronunciation. As explained in our{' '}
					<Link to="/privacy" style={styles.a}>Privacy Policy</Link>, this
					processing happens on your device and the recordings are not sent to
					us. You keep ownership of your recordings, and you grant us a limited
					license to process them solely to provide the Service. We do not sell
					your recordings or use them for advertising.
				</p>

				<h2 style={styles.h2}>Acceptable Use</h2>
				<p>You agree not to:</p>
				<ul>
					<li>Use the Service unlawfully or in violation of these Terms;</li>
					<li>Copy, modify, reverse-engineer, decompile, or create derivative works of the Service or its content, except where the law says you may;</li>
					<li>Scrape or harvest content, or access the Service or its systems without authorization;</li>
					<li>Interfere with or disrupt the Service&apos;s integrity, security, or performance.</li>
				</ul>

				<h2 style={styles.h2}>App Store Terms (Apple)</h2>
				<p>
					If you download the app from the Apple App Store, the following also
					apply: these Terms are between you and us only, not Apple, and Apple is
					not responsible for the app or its content. Apple has no obligation to
					provide maintenance or support for the app — that&apos;s our
					responsibility. To the maximum extent permitted by law, Apple has no
					warranty obligation for the app; if the app fails to conform to any
					applicable warranty, you may notify Apple and Apple may refund the
					purchase price, and Apple has no other warranty obligation. We, not
					Apple, are responsible for addressing any claims relating to the app
					(including product-liability, legal/regulatory, and
					intellectual-property claims). You represent that you are not in a
					U.S.-embargoed country or on a U.S. prohibited-parties list. Apple and
					its subsidiaries are third-party beneficiaries of these Terms and may
					enforce them. Your use of the app must also comply with the App Store
					Terms of Service; use from Google Play must comply with Google&apos;s
					terms.
				</p>

				<h2 style={styles.h2}>Disclaimers</h2>
				<p>
					The Service is provided &quot;as is&quot; and &quot;as available,&quot;
					without warranties of any kind, express or implied, to the maximum
					extent permitted by law. We don&apos;t warrant that the Service will be
					uninterrupted or error-free, or that practicing will produce any
					particular language-learning result.
				</p>

				<h2 style={styles.h2}>Limitation of Liability</h2>
				<p>
					To the maximum extent permitted by law, we will not be liable for
					indirect, incidental, special, consequential, or punitive damages, or
					for lost data or profits, arising from your use of the Service. Our
					total liability for any claim relating to the Service will not exceed
					the greater of the amount you paid us in the 12 months before the claim
					or USD $50. Some jurisdictions don&apos;t allow certain limitations, so
					some of these may not apply to you.
				</p>

				<h2 style={styles.h2}>Indemnification</h2>
				<p>
					You agree to indemnify and hold us harmless from claims and expenses
					arising out of your misuse of the Service or your violation of these
					Terms or applicable law.
				</p>

				<h2 style={styles.h2}>Termination</h2>
				<p>
					You may stop using the Service and request account deletion at any time
					(see the Privacy Policy). We may suspend or end your access if you
					violate these Terms or to protect the Service or its users. Sections
					that by their nature should survive termination — such as intellectual
					property, disclaimers, limitation of liability, and indemnification —
					survive.
				</p>

				<h2 style={styles.h2}>Governing Law</h2>
				<p>
					These Terms are governed by the laws of Italy, without regard to its
					conflict-of-laws rules, and the competent courts of Italy will have
					jurisdiction — except that, if you are a consumer, you also benefit from
					any mandatory consumer-protection rules of your country of residence,
					and nothing here deprives you of those protections.
				</p>

				<h2 style={styles.h2}>Changes to These Terms</h2>
				<p>
					We may update these Terms from time to time. We&apos;ll post the
					updated version here and change the &quot;Last updated&quot; date, and
					for material changes we&apos;ll provide additional notice. Continuing to
					use the Service after changes take effect means you accept them.
				</p>

				<h2 style={styles.h2}>Contact Us</h2>
				<p>
					Questions about these Terms? Contact us at{' '}
					<a style={styles.a} href="mailto:abogaard.dev@gmail.com">abogaard.dev@gmail.com</a>.
				</p>
				<p style={{ color: '#666' }}>
					Andrew Bogaard, Piuro (SO), Italy.
				</p>
			</div>
		</div>
	);
};

export default Terms;
